import OncolensToast from '@/components/OncolensToast';
import { LegacyAdminRoutes } from '@/utils/legacyRouteResolver';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import { Tooltip } from '@oncolens/oncolens-ui/lib/components/FormControls/Tooltip';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import React, { useEffect, useState } from 'react';
import { FaArrowRight, FaInfoCircle } from 'react-icons/fa';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useUserManager } from '@/hooks/featureFlags/active-flags/useUserManager';
import { XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import shallow from 'zustand/shallow';
import { useOrganizationHasLicense } from '../api/getOrgHasLicense';
import { useGetOrganization } from '../api/getOrganization';
import { getLegacyOrganizationId } from '../api/organizations';
import { useUpdateOrganization } from '../api/updateOrganization';
import { Licenses, OrganizationDto, OrganizationForUpdateDto } from '../types';
import { OrganizationLogoUpload } from './OrganizationLogoUpload';

type LegacyOrgLinkProps = {
  organization: OrganizationDto;
};

const LegacyOrgLink = ({ organization }: LegacyOrgLinkProps) => {
  const handleLegacyRedirect = () => {
    getLegacyOrganizationId(organization.id)
      .then((lookup) => {
        const url = LegacyAdminRoutes.hospital.edit(lookup.legacyId);

        window.open(url);
      })
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert(
          `'${organization.name}' isn't currently linked to a hospital or facility in the legacy system.`,
        );
      });
  };

  return (
    <Button
      onClick={handleLegacyRedirect}
      buttonColor="blue"
      buttonStyle="primary"
      className="inline-flex items-center w-auto py-2 mr-3">
      Edit (v1) <FaArrowRight className="ml-2" />
    </Button>
  );
};

type OrganizationFormProps = {
  organizationId: string | undefined;
};

const organizationSchema = yup.object<Record<keyof OrganizationForUpdateDto, yup.AnySchema>>({
  name: yup.string().required(),
  referenceCode: yup.string().required(),
  organizationSettings: yup.object().shape({
    requiresUserConsent: yup.boolean().required(),
    disallowPhi: yup.boolean().required(),
    showLabRecommendationsInRecommendationBuilder: yup.boolean().required(),
    virtualConferenceInfoPanelEnabled: yup.boolean().required(),
    virtualConferenceAudioRecording: yup.boolean().required(),
    autoFillFacilitiesForAccreditation: yup.boolean().required(),
    consentText: yup
      .string()
      .trim()
      .when('requiresUserConsent', {
        is: (requiresUserConsent: boolean) => requiresUserConsent,
        then: (schema) => schema.required('Consent Text is required'),
        otherwise: (schema) => schema.nullable(),
      }),
    treatmentPlanDisclaimer: yup.string().trim().nullable(),
    treatmentPlanTemplate: yup.string().trim().nullable(),
  }),
  address: yup.string().nullable(),
  city: yup.string().nullable(),
  state: yup.string().nullable(),
  zipCode: yup.string().nullable(),
  domains: yup.array().of(
    yup.string().matches(/^([A-Za-z0-9-]+\.)+[A-Za-z0-9-]+$/, {
      message: 'Invalid email domain',
    }),
  ),
  salesForceId: yup.string().nullable(),
});

const defaultValues = {
  name: '',
  referenceCode: '',
  organizationSettings: {
    requiresUserConsent: false,
    disallowPhi: false,
    showLabRecommendationsInRecommendationBuilder: false,
    virtualConferenceInfoPanelEnabled: false,
    virtualConferenceAudioRecording: false,
    autoFillFacilitiesForAccreditation: true,
    consentText: '',
    treatmentPlanDisclaimer: '',
    treatmentPlanTemplate: '',
  },
  address: '',
  city: '',
  state: '',
  zipCode: '',
  salesForceId: '',
  domains: [],
};

export const OrganizationForm = ({ organizationId }: OrganizationFormProps) => {
  const userManagerEnabled = useUserManager();
  const { data: organization } = useGetOrganization(organizationId);

  const formStore = useFormStore(defaultValues, organizationSchema);
  const [updateStore, isValid, getValues] = formStore(
    (x) => [x.updateStore, x.isValid, x.getValues],
    shallow,
  );
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const updateOrganizationMutation = useUpdateOrganization(organizationId);

  useEffect(() => {
    if (organization) {
      updateStore((x) => {
        Object.assign(x, organization);
      });
    }
  }, [organization, updateStore]);
  const onSave = () => {
    const store = formStore.getState();

    if (!store.isValid) return;

    setSaving(true);
    const dataToSave = store.getValues();
    updateOrganizationMutation.mutate(dataToSave, {
      onSuccess: () => OncolensToast.success('Organization saved'),
      onError: () => OncolensToast.error('An error occurred'),
      onSettled: () => setSaving(false),
    });
  };

  const { data: hasRekkyLicense } = useOrganizationHasLicense(
    organizationId,
    Licenses.MolecularRecommendations,
  );

  const { data: hasVirtualConferenceLicense } = useOrganizationHasLicense(
    organizationId,
    Licenses.VirtualConference,
  );

  return (
    <div className="rounded-md bg-white p-3 min-h-[32rem] ring-opacity-60 focus:outline-none">
      <div className="grid grid-cols-1 mb-4 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
        <FormField name="name" store={formStore}>
          {({ value, onChange, errorMessage }) => (
            <TextInput
              id="name"
              type="text"
              htmlName="name"
              label="Name"
              error={errorMessage}
              value={value}
              onChange={onChange}
              disabled={!!organizationId}
              wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
            />
          )}
        </FormField>
        <FormField name="referenceCode" store={formStore}>
          {({ value, onChange, errorMessage }) => (
            <TextInput
              id="organization.referenceCode"
              type="text"
              htmlName="referenceCode"
              label="Reference Code"
              error={errorMessage}
              value={value}
              onChange={onChange}
              wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
            />
          )}
        </FormField>
        <div className="grid grid-cols-2 col-span-6 md:col-span-3 gap-x-4">
          <FormField name="address" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextAreaInput
                id="organization.address"
                label="Address"
                value={value}
                onChange={onChange}
                htmlName="address"
                rows={2}
                error={errorMessage}
              />
            )}
          </FormField>
          <FormField name="city" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="organization.city"
                label="City"
                value={value}
                onChange={onChange}
                type="text"
                htmlName="city"
                error={errorMessage}
              />
            )}
          </FormField>
          <FormField name="state" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="organization.state"
                label="State"
                value={value}
                onChange={onChange}
                type="text"
                htmlName="state"
                error={errorMessage}
              />
            )}
          </FormField>
          <FormField name="zipCode" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="organization.zipCode"
                label="Zip Code"
                value={value}
                onChange={onChange}
                type="text"
                htmlName="zipCode"
                error={errorMessage}
              />
            )}
          </FormField>
          <div className="col-span-3 mt-4">
            <FormField name="organizationSettings.treatmentPlanTemplate" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <TextAreaInput
                  id="organization.organizationSettings.treatmentPlanTemplate"
                  label="Treatment Plan Template"
                  value={value}
                  onChange={onChange}
                  htmlName="treatmentPlanTemplate"
                  rows={8}
                  wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                  error={errorMessage}
                />
              )}
            </FormField>
            <FormField name="organizationSettings.treatmentPlanDisclaimer" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <TextAreaInput
                  id="organization.organizationSettings.treatmentPlanDisclaimer"
                  label="Treatment Plan Disclaimer"
                  value={value}
                  onChange={onChange}
                  htmlName="treatmentPlanDisclaimer"
                  rows={8}
                  wrapperProps={{ className: 'col-span-6 md:col-span-3 mt-4' }}
                  error={errorMessage}
                />
              )}
            </FormField>
          </div>
        </div>
        <div className="col-span-3">
          <FormField name="organizationSettings.disallowPhi" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <>
                <Checkbox
                  id="organization.organizationSettings.disallowPhi"
                  htmlName="disallowPhi"
                  label="Disallow Phi?"
                  value={value || false}
                  onClick={onChange}
                />
                {errorMessage ? <p className="mt-2 text-sm text-red-600">{errorMessage}</p> : null}
              </>
            )}
          </FormField>
          {hasRekkyLicense && (
            <FormField
              name="organizationSettings.showLabRecommendationsInRecommendationBuilder"
              store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.showLabRecommendationsInRecommendationBuilder"
                    htmlName="showLabRecommendationsInRecommendationBuilder"
                    label="Show Lab Recommendations in Recommendation Builder?"
                    value={value || false}
                    onClick={onChange}
                  />
                  {errorMessage ? (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                </>
              )}
            </FormField>
          )}
          <FormField name="organizationSettings.requiresUserConsent" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <>
                <Checkbox
                  id="organization.organizationSettings.requiresUserConsent"
                  htmlName="requiresUserConsent"
                  label="Requires User Consent?"
                  value={value || false}
                  onClick={onChange}
                />
                {errorMessage ? <p className="mt-2 text-sm text-red-600">{errorMessage}</p> : null}
              </>
            )}
          </FormField>
          <FormField
            name="organizationSettings.autoFillFacilitiesForAccreditation"
            store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <>
                <Checkbox
                  id="organization.organizationSettings.autoFillFacilitiesForAccreditation"
                  htmlName="autoFillFacilitiesForAccreditation"
                  label="Auto-fill Facilities for Accreditation"
                  value={value || false}
                  onClick={onChange}
                  data-cy="auto-fill-facilities-for-accreditation-checkbox"
                />
                {errorMessage ? (
                  <p
                    className="mt-2 text-sm text-red-600"
                    data-cy="auto-fill-facilities-for-accreditation-error">
                    {errorMessage}
                  </p>
                ) : null}
              </>
            )}
          </FormField>
          <div className="mt-4">
            <div className="flex items-center mb-2">
              <span
                className="text-sm font-medium text-gray-700"
                data-cy="virtual-conference-settings-label">
                Virtual Conference Settings
              </span>
              <Tooltip
                text="Enable the Virtual Conference license to configure these settings."
                icon={<FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />}
                data-cy="virtual-conference-settings-tooltip"
              />
            </div>
            <FormField
              name="organizationSettings.virtualConferenceInfoPanelEnabled"
              store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.virtualConferenceInfoPanelEnabled"
                    htmlName="virtualConferenceInfoPanelEnabled"
                    label="Allow admins to see the meeting info panel"
                    value={value || false}
                    onClick={onChange}
                    disabled={!hasVirtualConferenceLicense}
                  />
                  {errorMessage ? (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                </>
              )}
            </FormField>
            <FormField
              name="organizationSettings.virtualConferenceAudioRecording"
              store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.virtualConferenceAudioRecording"
                    htmlName="virtualConferenceAudioRecording"
                    label="Audio recording"
                    value={value || false}
                    onClick={onChange}
                    disabled={!hasVirtualConferenceLicense}
                  />
                  {errorMessage ? (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                </>
              )}
            </FormField>
          </div>
          <FormField
            name="organizationSettings.consentText"
            store={formStore}
            validationOptions={{
              validate: true,
              valueProvider: getValues,
            }}>
            {({ value, onChange, errorMessage }) => (
              <TextAreaInput
                id="organization.organizationSettings.consentText"
                label="Consent Text"
                value={value}
                onChange={onChange}
                htmlName="consentText"
                rows={8}
                wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                error={errorMessage}
              />
            )}
          </FormField>

          <FormField name="salesForceId" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="organization.salesForceId"
                type="text"
                htmlName="salesForceId"
                label="SalesForce ID"
                error={errorMessage}
                value={value}
                onChange={onChange}
                wrapperProps={{ className: 'col-span-6 md:col-span-3 mt-4' }}
              />
            )}
          </FormField>
          {organizationId && (
            <OrganizationLogoUpload
              organizationId={organizationId}
              currentLogoUrl={
                organization?.logoS3Key ? `/api/organizations/${organizationId}/logo` : undefined
              }
            />
          )}
        </div>
        {userManagerEnabled && (
          <FormField name="domains" store={formStore}>
            {({ value, onChange }) => (
              <>
                <DomainMultiSelect values={value ?? []} onChange={onChange} />
              </>
            )}
          </FormField>
        )}
      </div>
      <div className="flex items-center w-full">
        <Button
          className="w-auto px-6 ml-auto mr-3"
          buttonColor="gray"
          onClick={() => navigate(-1)}>
          Back
        </Button>
        {organization && <LegacyOrgLink organization={organization} />}
        <ButtonWithLoading
          loading={saving}
          className="w-auto px-6"
          buttonColor="blue"
          onClick={onSave}
          disabled={!isValid}>
          Save
        </ButtonWithLoading>
      </div>
    </div>
  );
};

interface DomainMultiSelectProps {
  values: string[];
  onChange: (newValues: string[]) => void;
}

function DomainMultiSelect({ values, onChange }: DomainMultiSelectProps) {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function validateDomains(newValues: string[]) {
    try {
      await organizationSchema.fields.domains.validate(newValues);
      setErrorMessage(null);
      onChange(newValues);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrorMessage(error.message);
      }
    }
  }

  function handleDomainEvent() {
    if (inputValue.trim()) {
      const newDomain = inputValue.trim();
      if (!values.includes(newDomain)) {
        validateDomains([...values, newDomain]);
      }
      setInputValue('');
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === 'Tab') {
      handleDomainEvent();
      e.preventDefault();
    }
  }

  function removeValue(valueToRemove: string) {
    validateDomains(values.filter((v) => v !== valueToRemove));
  }

  return (
    <div className="w-full gap-2 col-span-full ">
      <div className="flex flex-wrap items-center w-full lg:w-1/3">
        <TextInput
          placeholder="Enter email domain"
          id="domains"
          htmlName="domains"
          type="text"
          label="Email domains"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          inputProps={{
            onKeyDown: handleKeyDown,
          }}
          onBlur={handleDomainEvent}
          wrapperProps={{ className: 'col-span-full w-full' }}
        />
      </div>
      <div className="pt-2">
        {errorMessage && <p className="mb-2 text-sm text-rose-600">{errorMessage}</p>}
        <div className="flex space-x-2 ">
          {values.map((value) => (
            <span
              key={value}
              className="flex items-center h-8 px-3 py-2 text-sm font-medium text-white rounded-sm shadow select-none bg-oncolensBlue-500 border:blue-800 hover:bg-oncolensBlue-500/80">
              {value}
              <button
                type="button"
                onClick={() => removeValue(value)}
                className="p-0.5 -mr-1 ml-1 text-white">
                <XIcon className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
